import { useEffect, useState } from 'react';
import _ from 'lodash';
import { useDidUpdate, useLoader } from '@equifinance/shared';
import useAccountApi from '../../api/useAccountApi';
import {
  defaultAccount, useTransition, useSummary,
  useAccountContext,
} from '.';
import { Account } from './Account';
import { useProductsContext } from '../products';
import { useProgressStatusContext } from '../progressStatus/ProgressStatusProvider';

export function useAccount(id: string) {
  const [account, setAccount] = useState<Account>(defaultAccount);
  const [productsResultDigest, setProductsResultDigest] = useState<string | null>(null);
  const { setInProgress } = useProgressStatusContext();

  const incrementedUpdateCount = (prevUpdateCount: number) => (prevUpdateCount ? prevUpdateCount + 1 : 1);

  const updateAccount = async (field: string, value: any, disableIncrementUpdateCount: boolean = false) => {
    setAccount((prev) => {
      const clone = _.cloneDeep(prev);
      const newAccount = _.set(clone, field, value);
      return {
        ...newAccount,
        updateCount: (disableIncrementUpdateCount ? prev?.updateCount : incrementedUpdateCount(prev?.updateCount)),
        dirtyCount: incrementedUpdateCount(prev?.dirtyCount),
      };
    });
  };

  const { getAccount, postVerifyBankAccount } = useAccountApi();

  const fetchAccount = async (incrementUpdateCount: boolean = false) => {
    const result = await getAccount(id);

    setProductsResultDigest(result.productsResultDigest);

    setAccount((prev) => ({
      ...prev,
      ...result,
      updateCount: (incrementUpdateCount ? incrementedUpdateCount(prev?.updateCount) : prev?.updateCount),
    }));
  };

  const creditSearchCompleted = !!account?.applicationNumber;

  const { summary, fetchProductsSummary } = useSummary(id, productsResultDigest, creditSearchCompleted);

  const { transitionStatus, makeTransition, fetchTransitionStatus } = useTransition(fetchAccount, fetchProductsSummary, id);

  const verifyBankAccount = async () => {
    const { errors, warnings, ...rest } = await postVerifyBankAccount(account.id);
    setAccount((prev) => ({
      ...prev,
      ...rest,
      errors: errors ?? {},
      warnings: warnings ?? {},
    }));
    fetchTransitionStatus();
  };

  const { addLoader, removeLoader } = useLoader();

  useEffect(() => {
    (async () => {
      addLoader();
      await fetchAccount();
      removeLoader();
    })();
  }, [id]);

  return {
    account,
    summary,
    creditSearchCompleted,
    transitionStatus,
    makeTransition,
    fetchTransitionStatus,
    fetchAccount,
    updateAccount,
    setAccountData: setAccount,
    productsResultDigest,
    setProductsResultDigest,
    fetchProductsSummary,
    verifyBankAccount,
    async appendToAccountArray<T>(field: string, value: T, initiator: string = '') {
      setInProgress(initiator, true);
      setAccount((prev) => {
        const clone = _.cloneDeep(prev);
        const currentArray = _.get(clone, field) ?? [];

        const newAccount = _.set(clone, field, [...currentArray, value]);
        return {
          ...newAccount,
          initiator,
          updateCount: incrementedUpdateCount(prev?.updateCount),
          dirtyCount: incrementedUpdateCount(prev?.dirtyCount),
        };
      });
    },
    async deleteFromAccountArray(field: string, itemId: string) {
      setAccount((prev) => {
        const clone = _.cloneDeep(prev);
        const arr = _.get(clone, field);
        const newArray = arr.filter((item: { id: string }) => item.id !== itemId);
        const newAccount = _.set(clone, field, newArray);

        return {
          ...newAccount,
          updateCount: incrementedUpdateCount(prev?.updateCount),
          dirtyCount: incrementedUpdateCount(prev?.dirtyCount),
        };
      });
    },
  };
}

export const useAccountAutoSave = () => {
  const didUpdate = useDidUpdate();
  const {
    id, account, setAccountData, setProductsResultDigest, fetchTransitionStatus,
  } = useAccountContext();
  const { fetchProductLoanDetails } = useProductsContext();
  const { setInProgress } = useProgressStatusContext();

  const { putAccount } = useAccountApi();

  useEffect(() => {
    if (!didUpdate) return;
    (async () => {
      const { updateCount, dirtyCount, initiator } = account;
      const newAccount = await putAccount(account, id);
      setInProgress(initiator, false);

      await fetchTransitionStatus();
      const { productsResultDigest } = newAccount;
      setProductsResultDigest(productsResultDigest);

      setAccountData((prev: Account) => {
        if (updateCount >= prev?.updateCount && dirtyCount >= prev?.dirtyCount) {
          return {
            ...newAccount,
            updateCount,
          };
        }
        return prev;
      });

      await fetchProductLoanDetails();
    })();
  }, [account?.updateCount]);
};
